import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useLoading, useAuth } from '../check_login';
import creativeTypes from '../../creative_types/types';
import { IoArrowBack } from "react-icons/io5";
import TrumbowygEditor from '../../common/TrumbowygEditor';
import PlaceholderDropdown from '../../common/PlaceholderDropdown';
import TagsInput from '../../common/TagsInput';
import { motion } from 'framer-motion';
import htmlEmailTemplates from '../../html-versions/html-email';
import '../../../assets/css/addForm.css';
import '../../../assets/css/creativePreview.css';

const AddEmail = ({ pageTitle }) => {
  const navigate = useNavigate();
  const { showLoading, hideLoading } = useLoading();
  const { user: currentUser } = useAuth();

  // Permission check
  const permissions = currentUser?.role?.permissions || {};
  const canAITranslateEmail = permissions.canAITranslateEmail || currentUser?.isAdmin;

  const [creativeData, setCreativeData] = useState({
    title: '',
    tags: [],
    headline: '',
    body: '',
    creativeType: '',
    bodyBtnName: '',
    subjectLine1: '',
    subjectLine2: '',
    subjectLine3: '',
    preHeader: '',
    additionalText: '',
    additionalTextUnderBTN: '',
  });

  const [languages, setLanguages] = useState([]);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [translationProgress, setTranslationProgress] = useState(0);
  const [currentTranslatingLanguage, setCurrentTranslatingLanguage] = useState('');
  const [totalLanguages, setTotalLanguages] = useState(0);
  const [creationMode, setCreationMode] = useState(canAITranslateEmail ? 'translate' : 'manual'); // 'translate' or 'manual'
  const [manualLanguageData, setManualLanguageData] = useState({}); // Store manual input for each language
  const [previewLanguage, setPreviewLanguage] = useState(null); // Selected language for preview
  const [emptyLanguages, setEmptyLanguages] = useState([]); // Track languages with empty content
  const [brands, setBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [selectedVersion, setSelectedVersion] = useState('version1');
  
  // Status states
  const [statuses, setStatuses] = useState([]);
  const [statusSystemEnabled, setStatusSystemEnabled] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('');

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        const response = await axios.get('/brand/languages/active');
        setLanguages(response.data);
      } catch (err) {
        console.error('Error fetching languages:', err);
      }
    };
    const fetchBrands = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get('/brand/list', {
          headers: { Authorization: `Bearer ${token}` }
        });
        setBrands(response.data);
        if (response.data.length > 0) {
          setSelectedBrand(response.data[0]);
        }
      } catch (err) {
        console.error('Error fetching brands:', err);
      }
    };
    fetchLanguages();
    fetchBrands();
  }, []);

  useEffect(() => {
    const fetchStatusData = async () => {
      try {
        const token = localStorage.getItem('token');
        const [statusesRes, settingsRes] = await Promise.all([
          axios.get('/api/statuses/list', { headers: { Authorization: `Bearer ${token}` }}),
          axios.get('/api/statuses/settings', { headers: { Authorization: `Bearer ${token}` }})
        ]);
        setStatuses(statusesRes.data);
        setStatusSystemEnabled(settingsRes.data.isEnabled);

        const createdStatus = statusesRes.data.find((s) => s.name === 'Created');
        if (createdStatus) {
          setSelectedStatus(createdStatus._id);
        }
      } catch (err) {
        console.error('Error fetching status data:', err);
      }
    };
    fetchStatusData();
  }, []);

  const handleChange = (e) => {
    setCreativeData({ ...creativeData, [e.target.name]: e.target.value });
  };

  const toggleLanguageSelection = (languageId) => {
    if (selectAll) {
      setSelectAll(false);
      setSelectedLanguages(languages.map(l => l._id).filter(id => id !== languageId));
    } else if (selectedLanguages.includes(languageId)) {
      setSelectedLanguages(selectedLanguages.filter((id) => id !== languageId));
    } else {
      setSelectedLanguages([...selectedLanguages, languageId]);
    }
  };

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    setSelectedLanguages(!selectAll ? languages.map((lang) => lang._id) : []);
  };

  const handleManualLanguageChange = (languageId, field, value) => {
    setManualLanguageData(prev => ({
      ...prev,
      [languageId]: {
        ...prev[languageId],
        [field]: value
      }
    }));
    
    // Remove from empty languages if content is added
    if (value && value.trim() && (field === 'headline' || field === 'body' || field === 'subjectLine1')) {
      setEmptyLanguages(prev => prev.filter(id => id !== languageId));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setTranslationProgress(0);
    setCurrentTranslatingLanguage('');
    showLoading();

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('You are not logged in');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      const selectedLanguagesForApi = selectAll
        ? languages.map((lang) => lang._id)
        : selectedLanguages;

      if (selectedLanguagesForApi.length === 0) {
        setError('Please select at least one language.');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      // Validate status if system is enabled
      if (statusSystemEnabled && !selectedStatus) {
        setError('Please select a status.');
        hideLoading();
        setIsSubmitting(false);
        return;
      }

      if (creationMode === 'manual') {
        // Validate manual input for all selected languages
        const emptyLangs = [];
        for (const langId of selectedLanguagesForApi) {
          const langData = manualLanguageData[langId];
          if (!langData || !langData.headline || !langData.headline.trim() || !langData.body || !langData.body.trim() || !langData.bodyBtnName || !langData.bodyBtnName.trim() || !langData.subjectLine1 || !langData.subjectLine1.trim()) {
            emptyLangs.push(langId);
          }
        }
        
        if (emptyLangs.length > 0) {
          setEmptyLanguages(emptyLangs);
          const emptyLangNames = emptyLangs.map(id => {
            const lang = languages.find(l => l._id === id);
            return lang?.code?.toUpperCase() || lang?.languageName;
          }).join(', ');
          setError(`Please fill in all required fields for: ${emptyLangNames}`);
          hideLoading();
          setIsSubmitting(false);
          return;
        }

        // Submit manual creative data
        const manualCreatives = selectedLanguagesForApi.map(langId => {
          const langData = manualLanguageData[langId];
          const language = languages.find(l => l._id === langId);
          return {
            title: creativeData.title,
            tags: creativeData.tags,
            creativeType: creativeData.creativeType,
            ...langData,
            language: langId,
            languageName: language?.languageName || 'Unknown',
            ...(statusSystemEnabled && selectedStatus ? { status: selectedStatus } : {})
          };
        });

        const response = await axios.post('/creative/manual/email', {
          creatives: manualCreatives
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (response.data.success) {
          navigate('/library/creatives');
        }
        
      } else {
        // Original translate mode logic
        setTotalLanguages(selectedLanguagesForApi.length);

        // Get language names for progress tracking
        const selectedLanguageNames = selectedLanguagesForApi.map(langId => {
          const lang = languages.find(l => l._id === langId);
          return lang ? (lang.code?.toUpperCase() || lang.languageName) : 'Unknown';
        });

        const requestData = {
          ...creativeData,
          languages: selectedLanguagesForApi,
          ...(statusSystemEnabled && selectedStatus ? { status: selectedStatus } : {})
        };

        // Progress simulation
        const progressInterval = setInterval(() => {
          setTranslationProgress(prev => {
            if (prev < 90) {
              const increment = Math.random() * 15 + 5; // Random increment between 5-20%
              const newProgress = Math.min(prev + increment, 90);
              
              // Update current language being translated
              const currentLangIndex = Math.floor((newProgress / 90) * selectedLanguageNames.length);
              if (currentLangIndex < selectedLanguageNames.length) {
                setCurrentTranslatingLanguage(selectedLanguageNames[currentLangIndex]);
              }
              
              return newProgress;
            }
            return prev;
          });
        }, 800);

        const response = await axios.post('/translate/email', requestData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        clearInterval(progressInterval);
        setTranslationProgress(100);
        setCurrentTranslatingLanguage('Finalizing...');

        // Wait a moment to show 100%
        await new Promise(resolve => setTimeout(resolve, 500));

        if (response.status === 200) {
          navigate('/library/creatives');
        }
      }
    } catch (err) {
      console.error(err.response || err);
      setError(err.response?.data?.message || 'Error adding email');
    } finally {
      hideLoading();
      setIsSubmitting(false);
      setTranslationProgress(0);
      setCurrentTranslatingLanguage('');
    }
  };

  // Helper function for text stats calculation
  const calculateTextStats = (text) => {
    if (!text) return { characters: 0, words: 0 };
    // Strip HTML tags for accurate count
    const plainText = text.replace(/<[^>]*>/g, '');
    return {
      characters: plainText.length,
      words: plainText.trim().split(/\s+/).filter(word => word.length > 0).length
    };
  };

  // Email Preview Component
  const getLogoLinkForLanguage = (lang) => {
    if (!selectedBrand) return '';
    const langName = languages.find(l => l._id === lang)?.languageName;
    const langData = selectedBrand.langs.find((l) => l.languageName === langName);
    return langData ? langData.logoLink : '';
  };

  const renderEmailPreview = () => {
    if (!selectedBrand) {
      return (
        <div className="no-brand-selected">
          <p>Please create a brand first to preview the email</p>
        </div>
      );
    }



    // Get preview content based on mode and selected language
    let previewSubject = '';
    let previewPreHeader = '';
    let previewHeadline = '';
    let previewBody = '';
    let previewButton = '';
    let previewLang = '';
    
    if (creationMode === 'translate') {
      previewSubject = creativeData.subjectLine1 || 'Email Subject';
      previewPreHeader = creativeData.preHeader || '';
      previewHeadline = creativeData.headline || 'Email Headline';
      previewBody = creativeData.body || 'Email content will appear here...';
      previewButton = creativeData.bodyBtnName || '';
      // Use first selected language or first available language
      const langs = getPreviewLanguages();
      previewLang = langs.length > 0 ? langs[0]._id : (languages.length > 0 ? languages[0]._id : '');
    } else {
      if (previewLanguage && manualLanguageData[previewLanguage]) {
        previewSubject = manualLanguageData[previewLanguage].subjectLine1 || 'Email Subject';
        previewPreHeader = manualLanguageData[previewLanguage].preHeader || '';
        previewHeadline = manualLanguageData[previewLanguage].headline || 'Email Headline';
        previewBody = manualLanguageData[previewLanguage].body || 'Email content will appear here...';
        previewButton = manualLanguageData[previewLanguage].bodyBtnName || '';
        previewLang = previewLanguage;
      } else {
        const langs = getPreviewLanguages();
        previewLang = langs.length > 0 ? langs[0]._id : (languages.length > 0 ? languages[0]._id : '');
        previewSubject = 'Enter subject for selected language';
        previewHeadline = 'Enter headline for selected language';
        previewBody = 'Enter content for selected language';
        previewButton = '';
      }
    }

    const logoLink = getLogoLinkForLanguage(previewLang);

    const htmlContent = htmlEmailTemplates[selectedVersion](
      previewLang,
      creativeData.title || 'Email Title',
      previewHeadline,
      previewBody,
      previewButton,
      '', // bodyBtnLink
      previewSubject,
      creativeData.subjectLine2 || '',
      creativeData.subjectLine3 || '',
      previewPreHeader,
      '', // bodyImage
      '', // bodyImageLink
      creativeData.additionalText || '',
      manualLanguageData[previewLang]?.additionalTextUnderBTN || creativeData.additionalTextUnderBTN || '',
      selectedBrand.brandName,
      selectedBrand.disclaimer_marketing,
      selectedBrand.viewInBrowserURL,
      selectedBrand.frameColor,
      logoLink,
      selectedBrand.backgroundColor,
      selectedBrand.buttonGenralColor,
      selectedBrand.buttonTextColor,
      selectedBrand.logo,
      selectedBrand.smalldisclaimer,
      selectedBrand.borderRadius
    );

    return (
      <div className="email-preview-wrapper">
        <div 
          className="preview-content"
          dangerouslySetInnerHTML={{ __html: htmlContent }}
        />
      </div>
    );
  };

  // Get available languages for preview
  const getPreviewLanguages = () => {
    // Return selected languages for both modes
    return selectAll 
      ? languages 
      : languages.filter(lang => selectedLanguages.includes(lang._id));
  };

  // Set initial preview language when languages are selected
  useEffect(() => {
    if (creationMode === 'manual') {
      const availableLangs = getPreviewLanguages();
      if (availableLangs.length > 0) {
        setPreviewLanguage(availableLangs[0]._id);
      }
    } else {
      setPreviewLanguage(null);
    }
  }, [selectedLanguages, selectAll, languages, creationMode]);

  const validateAndSubmit = (e) => {
    e.preventDefault();

    if (!creativeData.creativeType) {
      setError('Please select a creative type.');
      return;
    }

    setError('');
    handleSubmit(e);
  };

  return (
    <div className="email-create-layout">
      {/* Header Section */}
      <div className="header-section">
        <motion.button 
          className="back-button"
          onClick={() => navigate(-1)}
          whileHover={{ scale: 1.05 }}
        >
          <IoArrowBack /> Back
        </motion.button>
        <h1 className="page-title">{pageTitle}</h1>
      </div>

      <div className="email-main-content">
        {/* Error Message */}
        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {/* Two Column Layout - Form and Preview */}
        <div className="form-with-preview">
          {/* Left Column - Form */}
          <motion.form 
          onSubmit={validateAndSubmit}
          className={`creative-form ${creationMode === 'manual' ? 'manual-mode' : ''}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
        >
        {/* Title Input */}
        <div className="form-group">
          <label className="input-label">Email Title</label>
          <input
            type="text"
            name="title"
            className="form-input"
            placeholder="Enter a title for your email"
            value={creativeData.title}
            onChange={handleChange}
            required
          />
        </div>

        {/* Tags Input */}
        <div className="form-group">
          <label className="input-label">Tags</label>
          <TagsInput
            value={creativeData.tags}
            onChange={(tags) => setCreativeData({ ...creativeData, tags })}
            placeholder="Type a tag and press Enter"
          />
        </div>

        {/* AI Translation / Manual Input Toggle */}
        {canAITranslateEmail && (
          <div className="form-group ai-mode-toggle">
            <label className="input-label">Content Mode</label>
            <div className="ai-toggle-row">
              <span className="ai-toggle-label">
                {creationMode === 'translate' ? 'AI Translation' : 'Manual Input'}
              </span>
              <label className="switch">
                <input
                  type="checkbox"
                  checked={creationMode === 'translate'}
                  onChange={(e) => setCreationMode(e.target.checked ? 'translate' : 'manual')}
                />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        )}

        {/* Creative Type Selector */}
        <div className="form-group">
          <label className="input-label">Creative Type</label>
          <select
            className="form-select"
            value={creativeData.creativeType}
            onChange={(e) => setCreativeData({ ...creativeData, creativeType: e.target.value })}
            required
          >
            <option value="">Select Creative Type</option>
            {Object.entries(creativeTypes).map(([key, value]) => (
              <option key={key} value={key}>{value}</option>
            ))}
          </select>
        </div>

        {/* Language Selection */}
        <div className="form-group">
          <label className="input-label">Languages</label>
          <div className="language-selector">
            <motion.div
              className={`language-option ${selectAll ? 'selected' : ''}`}
              onClick={toggleSelectAll}
              whileHover={{ scale: 1.02 }}
            >
              All Languages
            </motion.div>
            {languages.map((language) => (
              <motion.div
                key={language._id}
                className={`language-option ${(selectAll || selectedLanguages.includes(language._id)) ? 'selected' : ''}`}
                onClick={() => toggleLanguageSelection(language._id)}
                whileHover={{ scale: 1.02 }}
              >
                {language.code?.toUpperCase() || language.languageName}
              </motion.div>
            ))}
          </div>
        </div>

        {/* Status Selection - Show only if system is enabled */}
        {statusSystemEnabled && (
          <div className="form-group">
            <label className="input-label">Status <span className="required-asterisk">*</span></label>
            <select
              className="form-select"
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              required
            >
              <option value="">Select Status</option>
              {statuses.map((status) => (
                <option key={status._id} value={status._id}>
                  {status.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Conditional Content Based on Creation Mode */}
        {creationMode === 'translate' ? (
          <>
            {/* AI Translate Mode - Original Content */}
            {/* Email Subject Lines */}
            <div className="form-group">
              <label className="input-label">Subject Lines</label>
              <div className="subject-line-group">
            <div>
              <label className="input-label" style={{ fontSize: '0.875rem' }}>Subject Line 1</label>
              <input
                type="text"
                name="subjectLine1"
                className="form-input"
                placeholder="Subject Line 1"
                value={creativeData.subjectLine1}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="input-label" style={{ fontSize: '0.875rem' }}>Subject Line 2</label>
              <input
                type="text"
                name="subjectLine2"
                className="form-input"
                placeholder="Subject Line 2"
                value={creativeData.subjectLine2}
                onChange={handleChange}
                required
              />
            </div>
            <div>
              <label className="input-label" style={{ fontSize: '0.875rem' }}>Subject Line 3</label>
              <input
                type="text"
                name="subjectLine3"
                className="form-input"
                placeholder="Subject Line 3"
                value={creativeData.subjectLine3}
                onChange={handleChange}
                required
              />
            </div>
          </div>
        </div>

        {/* Pre Header */}
        <div className="form-group">
          <label className="input-label">Pre Header</label>
          <input
            type="text"
            name="preHeader"
            className="form-input"
            placeholder="Enter pre header text"
            value={creativeData.preHeader}
            onChange={handleChange}
            required
          />
        </div>

        {/* Headline */}
        <div className="form-group">
          <label className="input-label">Headline</label>
          <input
            type="text"
            name="headline"
            className="form-input"
            placeholder="Enter email headline"
            value={creativeData.headline}
            onChange={handleChange}
            required
          />
        </div>

        {/* Main Email Content */}
        <div className="form-group">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <label className="input-label">Email Body</label>
            <PlaceholderDropdown />
          </div>
          <div className="editor-container">
            <TrumbowygEditor
              value={creativeData.body}
              onEditorChange={(content) => setCreativeData({ ...creativeData, body: content })}
              init={{ height: 500 }}
            />
          </div>
        </div>

        {/* Button Text */}
        <div className="form-group">
          <label className="input-label">Button Text</label>
          <input
            type="text"
            name="bodyBtnName"
            className="form-input"
            placeholder="Enter button text"
            value={creativeData.bodyBtnName}
            onChange={handleChange}
            required
          />
        </div>

        {/* Additional Text */}
        <div className="form-group">
          <label className="input-label">Additional Text</label>
          <div className="editor-container">
            <TrumbowygEditor
              value={creativeData.additionalText}
              onEditorChange={(content) => setCreativeData({ ...creativeData, additionalText: content })}
              init={{ height: 200 }}
            />
          </div>
        </div>

        {/* Text Under Button */}
        <div className="form-group">
          <label className="input-label">Text Under Button</label>
          <div className="editor-container">
            <TrumbowygEditor
              value={creativeData.additionalTextUnderBTN}
              onEditorChange={(content) => setCreativeData({ ...creativeData, additionalTextUnderBTN: content })}
              init={{ height: 200 }}
            />
          </div>
        </div>
          </>
        ) : (
          <>
            {/* Manual Input Mode */}
            <div className="manual-languages-container">
              {(selectAll ? languages : languages.filter(lang => selectedLanguages.includes(lang._id))).map((language) => (
              <div key={language._id} className={`manual-language-section ${emptyLanguages.includes(language._id) ? 'error' : ''}`}>
                <h3 className="language-section-title">{language.code?.toUpperCase() || language.languageName}</h3>

                {/* Manual Subject Lines */}
                <div className="form-group">
                  <label className="input-label">Subject Lines ({language.code?.toUpperCase() || language.languageName})</label>
                  <div className="subject-line-group">
                    <div>
                      <label className="input-label" style={{ fontSize: '0.875rem' }}>Subject Line 1</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Subject Line 1"
                        value={manualLanguageData[language._id]?.subjectLine1 || ''}
                        onChange={(e) => handleManualLanguageChange(language._id, 'subjectLine1', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="input-label" style={{ fontSize: '0.875rem' }}>Subject Line 2</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Subject Line 2"
                        value={manualLanguageData[language._id]?.subjectLine2 || ''}
                        onChange={(e) => handleManualLanguageChange(language._id, 'subjectLine2', e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="input-label" style={{ fontSize: '0.875rem' }}>Subject Line 3</label>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="Subject Line 3"
                        value={manualLanguageData[language._id]?.subjectLine3 || ''}
                        onChange={(e) => handleManualLanguageChange(language._id, 'subjectLine3', e.target.value)}
                      />
                    </div>
                  </div>
                </div>                {/* Manual Pre-header */}
                <div className="form-group">
                  <label className="input-label">Pre-header ({language.code?.toUpperCase() || language.languageName})</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter pre-header text"
                    value={manualLanguageData[language._id]?.preHeader || ''}
                    onChange={(e) => handleManualLanguageChange(language._id, 'preHeader', e.target.value)}
                  />
                </div>

                {/* Manual Headline */}
                <div className="form-group">
                  <label className="input-label">Email Headline ({language.code?.toUpperCase() || language.languageName})</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter email headline"
                    value={manualLanguageData[language._id]?.headline || ''}
                    onChange={(e) => handleManualLanguageChange(language._id, 'headline', e.target.value)}
                    required
                  />
                </div>

                {/* Manual Body */}
                <div className="form-group">
                  <label className="input-label">Email Body ({language.code?.toUpperCase() || language.languageName})</label>
                  <div className="editor-container">
                    <TrumbowygEditor
                      value={manualLanguageData[language._id]?.body || ''}
                      onEditorChange={(content) => handleManualLanguageChange(language._id, 'body', content)}
                      init={{ height: 400 }}
                    />
                  </div>
                </div>

                {/* Manual Button Text */}
                <div className="form-group">
                  <label className="input-label">Button Text ({language.code?.toUpperCase() || language.languageName})</label>
                  <input
                    type="text"
                    className="form-input"
                    placeholder="Enter button text"
                    value={manualLanguageData[language._id]?.bodyBtnName || ''}
                    onChange={(e) => handleManualLanguageChange(language._id, 'bodyBtnName', e.target.value)}
                    required
                  />
                </div>

                {/* Manual Additional Text */}
                <div className="form-group">
                  <label className="input-label">Additional Text ({language.code?.toUpperCase() || language.languageName})</label>
                  <div className="editor-container">
                    <TrumbowygEditor
                      value={manualLanguageData[language._id]?.additionalText || ''}
                      onEditorChange={(content) => handleManualLanguageChange(language._id, 'additionalText', content)}
                      init={{ height: 200 }}
                    />
                  </div>
                </div>

                {/* Manual Text Under Button */}
                <div className="form-group">
                  <label className="input-label">Text Under Button ({language.code?.toUpperCase() || language.languageName})</label>
                  <div className="editor-container">
                    <TrumbowygEditor
                      value={manualLanguageData[language._id]?.additionalTextUnderBTN || ''}
                      onEditorChange={(content) => handleManualLanguageChange(language._id, 'additionalTextUnderBTN', content)}
                      init={{ height: 200 }}
                    />
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}

        {/* Submit Button */}
        <div className="form-actions">
          <motion.button 
            type="submit" 
            className="submit-button"
            disabled={isSubmitting}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.97 }}
          >
            {isSubmitting ? (
              <>
                <span className="button-spinner"></span>
                Creating...
              </>
            ) : (
              'Create Email'
            )}
          </motion.button>
        </div>
      </motion.form>

      {/* Right Column - Preview */}
      <div className="preview-panel">
        {/* Controls Bar - Above Preview */}
        <div className="preview-controls-bar">
          {/* Brand Selector */}
          <div className="preview-control-group">
            <label className="preview-control-label">Brand</label>
            <div className="preview-control-options">
              {brands.map((brand) => (
                <motion.button
                  key={brand._id}
                  type="button"
                  className={`preview-control-btn ${brand.brandName === selectedBrand?.brandName ? 'active' : ''}`}
                  onClick={() => setSelectedBrand(brand)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {brand.brandName}
                </motion.button>
              ))}
            </div>
          </div>

          {/* Language Selector for Manual Mode */}
          {creationMode === 'manual' && getPreviewLanguages().length > 0 && (
            <div className="preview-control-group">
              <label className="preview-control-label">Language</label>
              <div className="preview-control-options">
                {getPreviewLanguages().map((lang) => (
                  <motion.button
                    key={lang._id}
                    type="button"
                    className={`preview-control-btn ${previewLanguage === lang._id ? 'active' : ''}`}
                    onClick={() => setPreviewLanguage(lang._id)}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {lang.code?.toUpperCase() || lang.languageName}
                  </motion.button>
                ))}
              </div>
            </div>
          )}

          {/* Template Version Selector */}
          <div className="preview-control-group">
            <label className="preview-control-label">Template</label>
            <div className="preview-control-options">
              {Object.keys(htmlEmailTemplates).map((version) => (
                <motion.button
                  key={version}
                  type="button"
                  className={`preview-control-btn ${selectedVersion === version ? 'active' : ''}`}
                  onClick={() => setSelectedVersion(version)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {version.replace('version', 'V')}
                </motion.button>
              ))}
            </div>
          </div>
        </div>

        <div className="preview-header">
          <h3>Email Preview</h3>
        </div>
        <div className="preview-container">
          {renderEmailPreview()}
        </div>
      </div>
    </div>
    </div>

      {/* Enhanced Translation Loading Overlay */}
      {isSubmitting && (
        <div className="translation-loading-overlay">
          <div className="translation-loading-content">
            <div className="translation-header">
              <h2>Creating Email Creative</h2>
              <p>Please wait while we create your email in the selected languages</p>
            </div>
            
            <div className="translation-progress">
              <div className="progress-circle">
                <svg className="progress-ring" width="120" height="120">
                  <circle
                    className="progress-ring-circle-bg"
                    stroke="#e5e7eb"
                    strokeWidth="8"
                    fill="transparent"
                    r="52"
                    cx="60"
                    cy="60"
                  />
                  <circle
                    className="progress-ring-circle"
                    stroke="#4a90e2"
                    strokeWidth="8"
                    fill="transparent"
                    r="52"
                    cx="60"
                    cy="60"
                    style={{
                      strokeDasharray: `${2 * Math.PI * 52}`,
                      strokeDashoffset: `${2 * Math.PI * 52 * (1 - translationProgress / 100)}`,
                      transform: 'rotate(-90deg)',
                      transformOrigin: '50% 50%'
                    }}
                  />
                </svg>
                <div className="progress-text">
                  <span className="progress-percentage">{Math.round(translationProgress)}%</span>
                </div>
              </div>
              
              <div className="translation-info">
                <div className="translation-stats">
                  <div className="stat">
                    <span className="stat-label">Languages to create:</span>
                    <span className="stat-value">{totalLanguages}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">Progress:</span>
                    <span className="stat-value">{Math.round(translationProgress)}%</span>
                  </div>
                </div>
                
                {currentTranslatingLanguage && (
                  <div className="current-language">
                    <span className="current-language-label">Currently processing:</span>
                    <span className="current-language-name">{currentTranslatingLanguage}</span>
                  </div>
                )}
                
                <div className="progress-bar">
                  <div className="progress-bar-bg">
                    <div 
                      className="progress-bar-fill"
                      style={{ width: `${translationProgress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="translation-footer">
              <p className="translation-note">
                This process may take a few moments depending on the amount of content and number of languages.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddEmail;